import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, CardActions, Button } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import {
  PeopleOutlined,
  PersonAddOutlined,
  SearchOutlined,
  ArrowForwardRounded,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import OrganizationHeader from "../organization/components/OrganizationHeader";

export default function UserManagement() {
  const { t } = useTranslation();
  const theme = useTheme();

  const userManagementOptions = [
    {
      title: t("userManagement.usersLink"),
      description: t("userManagement.usersDesc", {
        defaultValue:
          "View, edit, and manage all users in your organization. Add new users, update permissions, and control access levels.",
      }),
      icon: <PeopleOutlined sx={{ fontSize: 36 }} />,
      path: "/usermanagement/userlist",
      color: theme.palette.primary.main,
      aria: t("userManagement.usersAria", {
        defaultValue: "Manage organization users",
      }),
    },
    {
      title: t("userManagement.searchUserLink"),
      description: t("userManagement.searchDesc", {
        defaultValue:
          "Search and filter users by various criteria including name, department, role, and user type for quick access.",
      }),
      icon: <SearchOutlined sx={{ fontSize: 36 }} />,
      path: "/usermanagement/search",
      color: theme.palette.secondary.main,
      aria: t("userManagement.searchAria", {
        defaultValue: "Search for specific users",
      }),
    },
  ];

  return (
    <>
      <OrganizationHeader
        icon={PersonAddOutlined}
        title={t("userManagement.headerTitle", {
          defaultValue: "User Management Center",
        })}
        subtitle={t("userManagement.headerSubtitle", {
          defaultValue: "Manage users, permissions, and organizational access",
        })}
      />

      {/* User Management Options Cards */}
      <Box sx={{ px: 3 }}>
        <Grid container spacing={3}>
          {userManagementOptions.map((option, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.3s ease",
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: theme.shadows[8],
                    borderColor: option.color,
                    "& .option-icon": {
                      transform: "scale(1.1)",
                      color: option.color,
                    },
                    "& .option-arrow": {
                      transform: "translateX(8px)",
                    },
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      className="option-icon"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 70,
                        height: 70,
                        borderRadius: 3,
                        bgcolor: alpha(option.color, 0.1),
                        border: "2px solid",
                        borderColor: alpha(option.color, 0.2),
                        color: alpha(option.color, 0.7),
                        transition: "all 0.3s ease",
                        mb: 2,
                      }}
                    >
                      {option.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ mb: 1, color: "text.primary", fontSize: "1rem" }}
                    >
                      {option.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.5,
                        fontSize: "0.8125rem",
                      }}
                    >
                      {option.description}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    component={Link}
                    to={option.path}
                    variant="contained"
                    fullWidth
                    endIcon={
                      <ArrowForwardRounded
                        className="option-arrow"
                        sx={{
                          fontSize: 18,
                          transition: "transform 0.3s ease",
                        }}
                      />
                    }
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      background: `linear-gradient(135deg, ${option.color} 0%, ${alpha(option.color, 0.8)} 100%)`,
                      "&:hover": {
                        boxShadow: theme.shadows[6],
                        background: `linear-gradient(135deg, ${alpha(option.color, 0.9)} 0%, ${alpha(option.color, 0.7)} 100%)`,
                      },
                      transition: "all 0.3s ease",
                    }}
                    aria-label={option.aria}
                  >
                    {t("userManagement.manage", { defaultValue: "Manage" })}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
}
